import { useCallback, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import type { Project, SiteSettings } from "@akhshy/types";
import { Polaroid } from "../molecules/Polaroid.tsx";
import { DeskItemCard } from "../molecules/DeskItemCard.tsx";
import { DeskDetailDialog } from "../molecules/DeskDetailDialog.tsx";
import { usePaginated } from "../../lib/usePaginated.ts";
import { client } from "../../lib/api.ts";
import { FALLBACK_PROJECTS } from "../../lib/fallback.ts";
import { buildDeskItems, SEARCH_ITEM_ID, type DeskItem } from "../../lib/deskItems.ts";
import type { DeskLayout, Placement } from "../../lib/deskGeometry.ts";
import { useDeskLayout, type DeskLayoutApi } from "../../lib/useDeskLayout.ts";
import { useCardDrag, type CardDragApi } from "../../lib/useCardDrag.ts";

const DESK_PROJECT_COUNT = 8;
const isSearchCard = (id: string) => id === SEARCH_ITEM_ID;

/** The CommandPalette listens for this event; same as the SearchBar on section pages. */
function openCommandPalette(): void {
  window.dispatchEvent(new Event("cmdk:open"));
}

/** Small fixed tilt per card for the stacked phone layout, so it still looks hand-placed. */
function stackedTilt(index: number): number {
  return ((index * 37) % 7) - 3;
}

function cardStyle(placement: Placement | undefined, z: number | undefined, index: number): CSSProperties {
  if (!placement) return { "--r": `${stackedTilt(index) * 0.4}deg` } as CSSProperties;
  return { "--x": `${placement.x}px`, "--y": `${placement.y}px`, "--r": `${placement.r}deg`, zIndex: z } as CSSProperties;
}

function deskStyle(layout: DeskLayout | null): CSSProperties | undefined {
  if (!layout) return undefined;
  return { height: layout.height, "--photo-top": `${layout.photoTop}px` } as CSSProperties;
}

function useDeskItems(): DeskItem[] | null {
  const { items, loading } = usePaginated<Project>(
    (page) => client.projects.list({ page, pageSize: DESK_PROJECT_COUNT }),
    [],
    FALLBACK_PROJECTS,
  );
  return useMemo(() => (loading ? null : buildDeskItems(items)), [loading, items]);
}

function DeskBar({ desk }: { desk: DeskLayoutApi }) {
  return (
    <div className="home-desk-bar">
      <p className="home-desk-hint">
        {desk.isStacked ? "tap a card to read it" : "drag things around, double-click to read one"}
      </p>
      {!desk.isStacked && (
        <button type="button" className="home-tidy" onClick={desk.toggleMode}>
          {desk.mode === "tidy" ? "Mess it up again" : "Tidy the desk"}
        </button>
      )}
    </div>
  );
}

interface DeskCardsProps {
  items: DeskItem[];
  desk: DeskLayoutApi;
  drag: CardDragApi;
  onOpen: (id: string) => void;
}

function DeskCards({ items, desk, drag, onOpen }: DeskCardsProps): ReactElement[] {
  return items.map((item, index) => (
    <DeskItemCard
      key={item.id}
      item={item}
      ref={desk.registerCard(item.id)}
      style={cardStyle(desk.layout?.placements[item.id], drag.zIndexOf(item.id), index)}
      className={item.id === drag.liftedId ? `is-lifted${drag.dragging ? " is-dragging" : ""}` : ""}
      onPointerDown={(e) => drag.onPointerDown(item.id, e)}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerEnd}
      onPointerCancel={drag.onPointerEnd}
      onKeyDown={(e) => drag.onKeyDown(item.id, e)}
      onDoubleClick={isSearchCard(item.id) ? undefined : () => onOpen(item.id)}
      onClick={() => drag.onClick(item.id)}
    />
  ));
}

/** The home desk: polaroid in the middle, hats, projects and stack as cards you can drag around. */
export function InteractiveDesk({ settings }: { settings: SiteSettings }) {
  const items = useDeskItems();
  const [openId, setOpenId] = useState<string | null>(null);
  const activate = useCallback((id: string) => (isSearchCard(id) ? openCommandPalette() : setOpenId(id)), []);
  const desk = useDeskLayout(items?.map((i) => i.id) ?? [], SEARCH_ITEM_ID);
  const drag = useCardDrag(desk, activate, isSearchCard);
  const selected = items?.find((i) => i.id === openId) ?? null;

  return (
    <section className="home-wrap home-desk-section" aria-label="The desk">
      <DeskBar desk={desk} />
      <div ref={desk.deskRef} className={`home-desk${desk.animate ? " is-animated" : ""}`} style={deskStyle(desk.layout)}>
        <div ref={desk.photoRef} className="home-desk-photo">
          <Polaroid src={settings.portraitUrl} caption={settings.portraitCaption} />
        </div>
        {items && <DeskCards items={items} desk={desk} drag={drag} onOpen={activate} />}
      </div>
      <DeskDetailDialog item={selected} onClose={() => setOpenId(null)} />
    </section>
  );
}
